// Establecimientos y puntos de emisión de Negocio: lo que las tarjetas calculan sin ir al backend.
//
// El backend (`backend/establishments.py`) vuelve a validar todo; aquí se valida antes para marcar el
// campo sin esperar la respuesta. Las claves de error son los nombres de campo del backend
// (`establishmentCode`, `establishmentAddress`, `emitionPointCode`, `seq_<tipo>`, `name`, `point`), así
// un 422 con `data.field` cae en el mismo lugar que un error local.

export const ADDRESS_MAX_LENGTH = 250;
export const NAME_MAX_LENGTH = 150;

// Último secuencial usado por tipo de comprobante, en el orden en que se piden.
export const SEQ_TYPES = [
    { key: 'receipt', label: 'Facturas' },
    { key: 'creditNote', label: 'Notas de crédito' },
    { key: 'remisionGuide', label: 'Guías de remisión' },
    { key: 'retention', label: 'Retenciones' },
    { key: 'proforma', label: 'Proformas' },
];

// Valores del selector «Factura desde» de la actividad.
export const NEW_ESTABLISHMENT = 'ne';
export const pointValue = (pointId) => 'pt:' + pointId;
export const newPointValue = (establishmentId) => 'np:' + establishmentId;

const CODE_ERROR = 'Usa de 1 a 3 dígitos, de 001 a 999.';
const ADDRESS_REQUIRED = 'Escribe la dirección: el SRI la exige en cada comprobante.';

export function emptySequentials() {
    return Object.fromEntries(SEQ_TYPES.map((s) => [s.key, '']));
}

// «2» -> «002»; null si no es un código de 001 a 999.
export function normalizeCode(raw) {
    const value = String(raw === null || raw === undefined ? '' : raw).trim();
    if (!/^[0-9]{1,3}$/.test(value) || parseInt(value, 10) === 0) return null;
    return value.padStart(3, '0');
}

const filled = (text) => String(text || '').trim() !== '';

export function validateAddress(address) {
    if (!filled(address)) return ADDRESS_REQUIRED;
    if (String(address).trim().length > ADDRESS_MAX_LENGTH) {
        return `La dirección admite hasta ${ADDRESS_MAX_LENGTH} caracteres.`;
    }
    return null;
}

export function validateSequentials(seq) {
    const errors = {};
    SEQ_TYPES.forEach((s) => {
        const value = String((seq && seq[s.key]) || '').trim();
        if (value && !/^[0-9]{1,9}$/.test(value)) {
            errors['seq_' + s.key] = 'Solo números enteros, sin puntos ni comas.';
        }
    });
    return errors;
}

// JSON de `startSequentials` con solo los tipos que se llenaron, o null.
export function sequentialsPayload(seq) {
    const filledOnes = SEQ_TYPES
        .filter((s) => filled(seq && seq[s.key]))
        .map((s) => [s.key, parseInt(String(seq[s.key]).trim(), 10)]);
    return filledOnes.length ? JSON.stringify(Object.fromEntries(filledOnes)) : null;
}

// Establecimiento nuevo: código libre, dirección, primer punto y secuenciales.
export function validateNewEstablishment(form, establishments) {
    const errors = {};
    const code = normalizeCode(form.code);
    if (!code) {
        errors.establishmentCode = CODE_ERROR;
    } else if ((establishments || []).some((e) => e.code === code)) {
        errors.establishmentCode = `Ya tienes el establecimiento ${code}. El siguiente libre es ${nextCode((establishments || []).map((e) => e.code))}.`;
    }
    if (!normalizeCode(form.pointCode)) errors.emitionPointCode = CODE_ERROR;
    const address = validateAddress(form.address);
    if (address) errors.establishmentAddress = address;
    if (String(form.name || '').trim().length > NAME_MAX_LENGTH) {
        errors.establishmentName = `El nombre admite hasta ${NAME_MAX_LENGTH} caracteres.`;
    }
    return Object.assign(errors, validateSequentials(form.seq));
}

// Punto nuevo dentro de un establecimiento que ya existe.
export function validateNewPoint(form, establishment) {
    const errors = {};
    const code = normalizeCode(form.pointCode);
    if (!code) {
        errors.emitionPointCode = CODE_ERROR;
    } else if (((establishment && establishment.points) || []).some((p) => p.code === code)) {
        errors.emitionPointCode = `${establishment.code}-${code} ya existe. El siguiente libre es ${establishment.nextPointCode}.`;
    }
    return Object.assign(errors, validateSequentials(form.seq));
}

// El siguiente al mayor, como el backend (`nextCode`).
export function nextCode(codes) {
    const used = new Set((codes || []).map((c) => parseInt(c, 10)).filter((n) => !Number.isNaN(n)));
    let candidate = used.size ? Math.max(...used) + 1 : 1;
    if (candidate > 999) {
        candidate = 1;
        while (used.has(candidate) && candidate <= 999) candidate++;
        if (candidate > 999) return null;
    }
    return String(candidate).padStart(3, '0');
}

export function hasAddress(establishment) {
    return !!establishment && filled(establishment.address);
}

export function missingAddressList(establishments) {
    return (establishments || []).filter((e) => !hasAddress(e));
}

export function findPoint(establishments, pointId) {
    for (const establishment of establishments || []) {
        const point = (establishment.points || []).find((p) => p.id === pointId);
        if (point) return { establishment, point };
    }
    return null;
}

const pad9 = (n) => String(n).padStart(9, '0');

// «001-001-000000128»: el último número de factura del punto, o null si nunca facturó.
export function lastReceiptNumber(point) {
    return point && point.receiptCount ? `${point.series}-${pad9(point.receiptCount)}` : null;
}

// «Factura desde»: puntos agrupados por establecimiento (Dropdown de PrimeVue con grupos), con la
// opción de abrir un punto nuevo en cada uno y, al final, un establecimiento nuevo.
export function pointOptions(establishments, ownActivityId = null) {
    const groups = (establishments || []).map((e) => ({
        label: `${e.code} · ${e.label} — ${hasAddress(e) ? e.address : 'sin dirección'}`,
        items: [
            ...(e.points || []).map((p) => {
                const others = (p.activities || []).filter((a) => a.id !== ownActivityId).map((a) => a.name);
                return {
                    value: pointValue(p.id),
                    label: `${p.series} · ${others.length ? 'usado por ' + others.join(', ') : 'libre'}`,
                };
            }),
            { value: newPointValue(e.id), label: `+ Nuevo punto en ${e.code}…` },
        ],
    }));
    groups.push({ label: 'Otro local', items: [{ value: NEW_ESTABLISHMENT, label: '+ Nuevo establecimiento…' }] });
    return groups;
}

// Lo que el selector manda al backend en `activity/save`, según la opción elegida.
export function activityPointFields(choice, form) {
    if (!choice) return {};
    if (choice.startsWith('pt:')) return { point: choice.slice(3) };
    const seq = sequentialsPayload(form.seq);
    if (choice.startsWith('np:')) {
        return { establishment: choice.slice(3), emitionPointCode: form.pointCode, startSequentials: seq };
    }
    return {
        establishmentCode: form.code, establishmentName: form.name, establishmentAddress: form.address,
        emitionPointCode: form.pointCode, startSequentials: seq,
    };
}

// Las otras actividades que facturan desde el punto elegido (comparten la numeración).
export function sharedWith(establishments, pointId, ownActivityId = null) {
    const found = findPoint(establishments, pointId);
    if (!found) return [];
    return (found.point.activities || []).filter((a) => a.id !== ownActivityId).map((a) => a.name);
}

// Campo que el backend marcó en un 422 (`data.field`), si lo hay.
export function serverField(response) {
    return (response && response.data && response.data.field) || null;
}
